import NotificationModal from './NotificationModal'
import { alertDialog, confirmDialog } from '../lib/systemDialogs'
import { createVisiblePoller } from '../lib/visiblePoller'
import PageContent from './PageContent'
import StableFeedback from './StableFeedback'
import { PAGE_REFRESH_ERROR } from '../lib/usePageRefresh'
import '../page-stability.css'
import NotificationInbox from './NotificationInbox'
import DevicePushMenu from './DevicePushMenu'
import UiToolbar from './UiToolbar'
import UiCustomText from './UiCustomText'
import { recoverablePage as lazyPage } from '../lib/recoverablePage'
import PageErrorBoundary from './PageErrorBoundary'
const LayoutDesigner = lazyPage(() => import('./LayoutDesigner'))
import BackToTop from './BackToTop'
import PopupNotifications from './PopupNotifications'
import BookingNotificationPopup from './BookingNotificationPopup'
import OnlineBookingPopup from './OnlineBookingPopup'
import MissingCheckinPopup from './MissingCheckinPopup'
import { canSeeMissingCheckins } from '../lib/missingCheckinAudience'
import { BellRing, Bot, Cake, CalendarDays, CircleDollarSign, ClipboardList, Compass, ExternalLink, FileSignature, FileText, HardDrive, LogOut, Menu, RadioTower, RefreshCw, ScanLine, Server, Settings2, UserRound, Users, WalletCards, X } from 'lucide-react'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { veraApi } from '../lib/api'
import { checkAttendanceBreakAlerts, deleteAttendanceBreakAlertForAll, getAttendanceBreakAlertControl, setAttendanceBreakAlertControl, syncPersistentBreakNotifications } from '../lib/attendanceBreakAlerts'

const items = [
  { id: 'live-tour', label: 'Live Tour', icon: RadioTower, ready: true },
  { id: 'milk-tea', label: 'Trà sữa', icon: CircleDollarSign, ready: true, roles: ['leader', 'nhanvien'] },
  { id: 'leave', label: 'Đăng ký nghỉ', icon: CalendarDays, ready: true, roles: ['admin', 'quanly', 'letan', 'leader', 'nhanvien'] },
  { id: 'schedule', label: 'Lịch làm việc', icon: CalendarDays, ready: true, anyPermission: ['work_schedule_quanly', 'work_schedule_letan', 'work_schedule_locker', 'work_schedule_tapvu', 'work_schedule_hc'] },
  { id: 'online-bookings', label: 'Booking online', icon: CalendarDays, ready: true, roles: ['admin', 'quanly', 'letan', 'locker'] },
  { id: 'snapshot', label: 'Chấm công', icon: ScanLine, ready: true, anyPermission: ['snapshot_today', 'device_history_view'] },
  { id: 'payroll', label: 'Bảng Lương', icon: WalletCards, ready: true, anyPermission: ['payroll_history', 'payroll_calculate'] },
  { id: 'reports', label: 'Báo cáo', icon: FileText, ready: true, permission: 'live_tour_reports_view' },
  { id: 'revenue', label: 'Doanh thu', icon: CircleDollarSign, ready: true, permission: 'revenue_view' },
  { id: 'quang-ngai', label: 'Quảng Ngãi', icon: CircleDollarSign, ready: true, roles: ['admin', 'giamdoc'] },
  { id: 'employees', label: 'Nhân viên', icon: Users, ready: true, permission: 'staff_list' },
  { id: 'customers', label: 'Khách hàng', icon: Users, ready: true, permission: 'live_tour_customers_view' },
  { id: 'purchases', label: 'Nhập mua', icon: CircleDollarSign, ready: true, permission: 'purchase_view' },
  { id: 'contract-1', label: 'Hợp đồng', icon: FileSignature, ready: true, permission: 'contract_1_view' },
  { id: 'birthday', label: 'Sinh nhật', icon: Cake, ready: true, permission: 'birthday' },
  { id: 'long-leave', label: 'Phép năm', icon: ClipboardList, ready: true, anyPermission: ['long_leave', 'long_leave_form', 'long_leave_stats', 'resignation_form'] },
  { id: 'hr', label: 'Nhân sự', icon: Users, ready: true, adminOnly: true },
  { id: 'training', label: 'Đào tạo', icon: ClipboardList, ready: true, permission: 'training_view' },
  { id: 'profile', label: 'Hồ sơ', icon: UserRound, ready: true, permission: 'profile' },
  { id: 'auto-check', label: 'Auto Check', icon: Bot, ready: true, permission: 'auto_penalty' },
  { id: 'system', label: 'Hệ thống', icon: HardDrive, ready: true, anyPermission: ['audit_admin_view', 'storage_admin_view'] },
  { id: 'settings', label: 'Cài đặt', icon: Settings2, ready: true, anyPermission: ['live_tour_admin', 'ktv_shift_view', 'work_schedule_letan', 'work_schedule_locker', 'work_schedule_quanly', 'work_schedule_tapvu', 'permission_admin'] },
  { id: 'rules', label: 'Nội qui', icon: FileText, ready: true, permission: 'official_rules_view' },
  { id: 'residence', label: 'Tạm trú', icon: Users, ready: true, permission: 'residence_view' },
  { id: 'devices', label: 'Thiết bị', icon: Server, ready: true, permission: 'device_view' },
]

const BREAK_ALERT_DISMISSED_KEY = 'vera-break-alerts-admin-dismissed'
const BREAK_ALERT_POSITION_KEY = 'vera-break-alert-position-v2'
const BREAK_ALERT_POLL_MS = 15 * 1000

const readDismissedBreakAlerts = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(BREAK_ALERT_DISMISSED_KEY) || '[]')
    return Array.isArray(value) ? value.filter(Boolean) : []
  } catch {
    return []
  }
}

const writeDismissedBreakAlerts = (tags) => {
  try { window.localStorage.setItem(BREAK_ALERT_DISMISSED_KEY, JSON.stringify([...new Set(tags.filter(Boolean))])) } catch { /* ignore storage failures */ }
}

const filterAdminDismissedAlerts = (alerts, role) => {
  if (role !== 'admin') return alerts
  const activeTags = new Set((alerts || []).map((item) => item.tag).filter(Boolean))
  const dismissed = readDismissedBreakAlerts().filter((tag) => activeTags.has(tag))
  writeDismissedBreakAlerts(dismissed)
  const dismissedSet = new Set(dismissed)
  return (alerts || []).filter((item) => !item.tag || !dismissedSet.has(item.tag))
}

const readAlertPosition = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(BREAK_ALERT_POSITION_KEY) || '{}')
    const left = Number(value.left)
    const top = Number(value.top)
    if (Number.isFinite(left) && Number.isFinite(top)) return { left, top }
  } catch { /* use default */ }
  return { left: null, top: 82 }
}

const durationText = (seconds) => {
  const total = Math.max(0, Math.floor(Math.abs(Number(seconds || 0))))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  return `${hours ? `${hours}:` : ''}${`${minutes}`.padStart(2, '0')}:${`${secs}`.padStart(2, '0')}`
}

const liveAlertTiming = (alert, nowMs) => {
  const deadlineMs = alert?.deadline_iso ? new Date(alert.deadline_iso).getTime() : NaN
  if (!Number.isFinite(deadlineMs)) {
    const late = Number(alert?.late_seconds || 0)
    return late > 0 ? `Đang trễ ${durationText(late)}` : `Còn ${durationText(alert?.remaining_seconds || 0)}`
  }
  const delta = Math.floor((deadlineMs - nowMs) / 1000)
  return delta >= 0 ? `Còn ${durationText(delta)}` : `Đang trễ ${durationText(-delta)}`
}

export default function AppShell({ user, currentPage, standalone = false, onPageChange, onPageIntent, onRefreshCurrentPage, onSignOut, children }) {
  const showPageNotifications = true
  const [refreshMessage, setRefreshMessage] = useState('')
  useEffect(() => {
    setRefreshMessage('')
    const failed = event => setRefreshMessage(event.detail)
    window.addEventListener(PAGE_REFRESH_ERROR, failed)
    return () => window.removeEventListener(PAGE_REFRESH_ERROR, failed)
  }, [currentPage])
  const refreshPage = () => {
    setRefreshMessage('')
    if (onRefreshCurrentPage?.() === false) setRefreshMessage('Có thay đổi chưa lưu hoặc tác vụ đang xử lý. Hãy hoàn tất rồi làm mới.')
  }
  const [mobileOpen, setMobileOpen] = useState(false)
  const layoutTrigger = useRef(null)
  const [layoutDesignerOpen, setLayoutDesignerOpen] = useState(currentPage === 'appearance' || (user?.role === 'admin' && new URLSearchParams(window.location.search).get('layoutPreview') === 'mobile'))
  const [standaloneMenuOpen, setStandaloneMenuOpen] = useState(false)
  const [birthdayNotice, setBirthdayNotice] = useState(null)
  const [notificationSettings, setNotificationSettings] = useState(null)
  const [breakAlerts, setBreakAlerts] = useState([])
  const [breakAlertsHidden, setBreakAlertsHidden] = useState(false)
  const [breakAlertControl, setBreakAlertControl] = useState({ disabled: false, busy: false })
  const [breakAlertPosition, setBreakAlertPosition] = useState(readAlertPosition)
  const [clockMs, setClockMs] = useState(Date.now())
  const [deletingBreakAlertTag, setDeletingBreakAlertTag] = useState('')
  const breakAlertStackRef = useRef(null)
  const dragRef = useRef(null)
  const menuSwipeRef = useRef(null)
  const role = String(user?.role || '').toLowerCase()
  const isAdmin = role === 'admin'

  useEffect(() => {
    let active = true
    const loadSettings=()=>veraApi.notificationSettings().then((result) => {
      if (active) setNotificationSettings(Object.fromEntries((result.settings || []).flatMap((item) => [[item.key, item.enabled && item.channel_enabled?.in_app !== false],[`${item.key}_routed`,item.routed],[`${item.key}_has_rules`,item.has_rules]])))
    }).catch(() => { if (active) setNotificationSettings({}) })
    const settingsPoller = createVisiblePoller(loadSettings, { interval: 60000 })
    window.addEventListener('vera-notification-settings-changed',settingsPoller.refresh)
    return () => { active = false;settingsPoller.stop();window.removeEventListener('vera-notification-settings-changed',settingsPoller.refresh) }
  }, [user?.employee_username])

  useEffect(() => {
    const viewerRole = String(user?.role || '').toLowerCase()
    const currentDay = new Date().getDate()
    if(notificationSettings?.birthday_routed || notificationSettings?.birthday === false) setBirthdayNotice(null)
    if (!notificationSettings || notificationSettings.birthday === false || user?.must_change_password || !user?.permissions?.birthday || !['admin', 'quanly', 'letan'].includes(viewerRole) || currentDay > 5) return
    const today = new Date().toISOString().slice(0, 10)
    if (window.localStorage.getItem('vera-birthday-dismissed') === today) return
    veraApi.birthdays().then((result) => {
      if ((result.birthdays || []).length) {
        void veraApi.routeLocalNotification('birthday').catch(()=>{})
        if(!notificationSettings.birthday_routed) setBirthdayNotice(result)
      }
    }).catch(() => {})
  }, [notificationSettings, user?.must_change_password, user?.permissions?.birthday, user?.role])

  useEffect(() => {
    if (user?.must_change_password) {
      setBreakAlerts([])
      void syncPersistentBreakNotifications([])
      return undefined
    }
    if (!['admin', 'quanly', 'letan', 'nhanvien', 'leader'].includes(role)) return undefined
    let stopped = false
    let running = false
    const poll = async () => {
      if (running || stopped || document.hidden) return
      running = true
      try {
        let control = { disabled: false }
        try { control = await getAttendanceBreakAlertControl() } catch { /* old backend fallback */ }
        if (stopped) return
        const disabled = Boolean(control?.disabled)
        setBreakAlertControl((current) => ({ ...current, disabled }))
        if (disabled) {
          setBreakAlerts([])
          await syncPersistentBreakNotifications([])
          return
        }
        const result = await checkAttendanceBreakAlerts()
        if (stopped) return
        const alerts = filterAdminDismissedAlerts(result.alerts || [], role).filter(item => !canSeeMissingCheckins(role) || item.kind !== 'missing-scheduled-checkin')
        setBreakAlerts(alerts)
        setClockMs(Date.now())
        await syncPersistentBreakNotifications(alerts)
      } catch {
        // Break-alert polling must never interrupt normal Web V2 navigation.
      } finally {
        running = false
      }
    }
    const breakPoller = createVisiblePoller(poll, { interval: BREAK_ALERT_POLL_MS })
    const onFocus = () => { void breakPoller.refresh() }
    window.addEventListener('focus', onFocus)
    return () => {
      stopped = true
      breakPoller.stop()
      window.removeEventListener('focus', onFocus)
    }
  }, [role, user?.must_change_password])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined
    const onMessage = (event) => {
      const message = event?.data || {}
      if (message.type === 'attendance-break-cleared' && message.tag) {
        setBreakAlerts((current) => current.filter((item) => item.tag !== message.tag))
      }
      if (message.type === 'attendance-break-global-disabled') {
        setBreakAlerts([])
        setBreakAlertsHidden(false)
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    if (!breakAlerts.length) return undefined
    const timer = window.setInterval(() => setClockMs(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [breakAlerts.length])

  useEffect(() => {
    if (!Number.isFinite(Number(breakAlertPosition.left))) return
    try { window.localStorage.setItem(BREAK_ALERT_POSITION_KEY, JSON.stringify(breakAlertPosition)) } catch { /* ignore */ }
  }, [breakAlertPosition])

  const dismissBirthday = () => {
    window.localStorage.setItem('vera-birthday-dismissed', new Date().toISOString().slice(0, 10))
    setBirthdayNotice(null)
  }

  const dismissBreakAlert = (alert) => {
    if (!isAdmin || !alert?.tag) return
    const dismissed = new Set(readDismissedBreakAlerts())
    dismissed.add(alert.tag)
    writeDismissedBreakAlerts([...dismissed])
    const next = breakAlerts.filter((item) => item.tag !== alert.tag)
    setBreakAlerts(next)
    void syncPersistentBreakNotifications(next)
  }

  const deleteBreakAlertForAll = async (alert) => {
    if (!isAdmin || !alert?.key || !alert?.tag || deletingBreakAlertTag) return
    const accepted = (await confirmDialog(`Xóa hoàn toàn cảnh báo của ${alert.employee} cho tất cả tài khoản? Cảnh báo này sẽ không xuất hiện lại.`))
    if (!accepted) return
    setDeletingBreakAlertTag(alert.tag)
    try {
      await deleteAttendanceBreakAlertForAll(alert.key, alert.tag)
      const dismissed = readDismissedBreakAlerts().filter((tag) => tag !== alert.tag)
      writeDismissedBreakAlerts(dismissed)
      const next = breakAlerts.filter((item) => item.tag !== alert.tag)
      setBreakAlerts(next)
      await syncPersistentBreakNotifications(next)
    } catch (error) {
      (await alertDialog(error?.message || 'Không xóa được cảnh báo cho tất cả tài khoản.'))
    } finally {
      setDeletingBreakAlertTag('')
    }
  }

  const toggleGlobalBreakAlerts = async (disabled) => {
    if (!isAdmin || breakAlertControl.busy) return
    setBreakAlertControl((current) => ({ ...current, busy: true }))
    try {
      const result = await setAttendanceBreakAlertControl(disabled)
      const nextDisabled = Boolean(result?.disabled)
      setBreakAlertControl({ disabled: nextDisabled, busy: false })
      if (nextDisabled) {
        setBreakAlerts([])
        setBreakAlertsHidden(false)
        await syncPersistentBreakNotifications([])
      }
    } catch {
      setBreakAlertControl((current) => ({ ...current, busy: false }))
    }
  }

  const beginAlertDrag = (event) => {
    if (event.target.closest('button')) return
    const element = breakAlertStackRef.current
    if (!element) return
    const rect = element.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const moveAlertDrag = (event) => {
    const drag = dragRef.current
    const element = breakAlertStackRef.current
    if (!drag || !element || drag.pointerId !== event.pointerId) return
    const rect = element.getBoundingClientRect()
    const maxLeft = Math.max(6, window.innerWidth - rect.width - 6)
    const maxTop = Math.max(6, window.innerHeight - Math.min(rect.height, window.innerHeight - 12) - 6)
    const left = Math.max(6, Math.min(maxLeft, event.clientX - drag.offsetX))
    const top = Math.max(6, Math.min(maxTop, event.clientY - drag.offsetY))
    setBreakAlertPosition({ left, top })
  }

  const endAlertDrag = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }

  const alertPositionStyle = Number.isFinite(Number(breakAlertPosition.left))
    ? { left: `${breakAlertPosition.left}px`, top: `${breakAlertPosition.top}px` }
    : { right: '18px', top: `${breakAlertPosition.top}px` }

  const choose = (id, ready) => {
    if (!ready || (user?.must_change_password && id !== 'profile')) return
    onPageChange(id)
    setMobileOpen(false)
    setStandaloneMenuOpen(false)
  }

  const menuPageUrl = (id) => {
    const url = new URL(window.location.href)
    url.searchParams.set('page', id)
    url.searchParams.set('standalone', '1')
    return url.toString()
  }

  const chooseFromLink = (event, id, ready) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    choose(id, ready)
  }

  const beginMenuSwipe = (event) => {
    if (event.pointerType !== 'touch') return
    const target = event.target
    if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) return
    menuSwipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      sidebarWasOpen: sidebarOpen,
    }
  }

  const endMenuSwipe = (event) => {
    const gesture = menuSwipeRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    menuSwipeRef.current = null
    const dx = event.clientX - gesture.startX
    const dy = event.clientY - gesture.startY
    const horizontal = Math.abs(dx) >= 70 && Math.abs(dx) > Math.abs(dy) * 1.25
    if (!horizontal) return
    if (!gesture.sidebarWasOpen && dx > 0 && gesture.startX <= 48) {
      if (standalone) setStandaloneMenuOpen(true)
      else setMobileOpen(true)
      return
    }
    if (gesture.sidebarWasOpen && dx < 0) {
      setMobileOpen(false)
      setStandaloneMenuOpen(false)
    }
  }

  const cancelMenuSwipe = (event) => {
    if (menuSwipeRef.current?.pointerId === event.pointerId) menuSwipeRef.current = null
  }

  const openCurrentPageInNewTab = () => {
    const url = new URL(window.location.href)
    url.searchParams.set('page', currentPage)
    url.searchParams.set('standalone', '1')
    window.open(url.toString(), '_blank', 'noopener,noreferrer')
  }

  const sidebarOpen = mobileOpen || (standalone && standaloneMenuOpen)
  const navigationToggle = useMemo(() => standalone
    ? <button data-ui-key="u-c7ea03a89a43" type="button" className="standalone-menu-toggle icon-button" onClick={() => setStandaloneMenuOpen((value) => !value)} aria-label={standaloneMenuOpen ? 'Ẩn Menu' : 'Hiện Menu'} aria-expanded={standaloneMenuOpen}>{standaloneMenuOpen ? <X size={20} /> : <Menu size={20} />} {standaloneMenuOpen ? 'Ẩn Menu' : 'Hiện Menu'}</button>
    : <button data-ui-key="u-b36248799e5c" type="button" className="mobile-menu icon-button" onClick={() => setMobileOpen(true)} aria-label="Mở menu" aria-expanded={mobileOpen}><Menu size={22} /></button>, [standalone, standaloneMenuOpen, mobileOpen])

  return (
    <div className={`app-shell ${standalone ? `standalone-mode ${standaloneMenuOpen ? 'menu-open' : 'menu-hidden'}` : ''}`} onPointerDown={beginMenuSwipe} onPointerUp={endMenuSwipe} onPointerCancel={cancelMenuSwipe}>
      {/* Canonical phrase retained for CI/history: Suối nguồn thư giãn, trọn vẹn an yên. */}
      {/* Refresh updates data in place; keep the mounted page, filters and scroll containers. */}
      <style>{`
        .topbar-title.vera-script-tagline{font-family:'Lavishly Yours',cursive;font-size:28px;font-weight:700;line-height:1;letter-spacing:.01em;color:#173329;white-space:nowrap}
        .page-wrap.live-tour-page-wrap{padding-block:0}
        .app-shell.standalone-mode.menu-hidden{grid-template-columns:minmax(0,1fr)}.app-shell.standalone-mode .sidebar.standalone-hidden{display:none}.standalone-menu-toggle{display:inline-flex;align-items:center;gap:6px;width:auto;padding:7px 10px;font-size:12px;font-weight:850;white-space:nowrap}.topbar-actions{display:flex;align-items:center;gap:7px;margin-left:auto}.topbar-actions .topbar-refresh-button{margin-left:0}.topbar-open-tab-button{background:#fff}
        .break-alert-stack{position:fixed;z-index:1200;width:min(410px,calc(100vw - 20px));max-height:calc(100vh - 90px);overflow-y:auto;display:grid;gap:6px;margin:0;pointer-events:auto}.break-alert-toolbar{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:6px 8px;border-radius:10px;background:#173d31;color:white;box-shadow:0 5px 16px rgba(31,54,46,.18);cursor:move;touch-action:none;user-select:none}.break-alert-toolbar strong{font-size:12px;color:white}.break-alert-toolbar-actions{display:flex;align-items:center;gap:5px}.break-alert-toolbar button,.break-alert-card button,.break-alert-hidden-chip button,.break-alert-global-off button{border:1px solid currentColor;background:#fff;border-radius:7px;padding:4px 7px;font-size:11px;font-weight:800;cursor:pointer}.break-alert-toolbar button{color:#173d31}.break-alert-card{display:grid;grid-template-columns:auto minmax(0,1fr);gap:7px;align-items:flex-start;padding:8px 10px;border:1px solid #a92c25;border-radius:10px;background:#fff6f4;box-shadow:0 5px 16px rgba(120,24,17,.13)}.break-alert-card.employee{border-color:#c98212;background:#fff9ed}.break-alert-card>svg{margin-top:1px;color:#a92c25}.break-alert-card.employee>svg{color:#a46708}.break-alert-card strong{display:block;font-size:12px;line-height:1.3;color:#8d211b}.break-alert-card.employee strong{color:#8b5a05}.break-alert-card span{display:block;margin-top:2px;font-size:11px;line-height:1.35;color:#543d38}.break-alert-card .break-alert-timer{font-weight:900;font-size:12px}.break-alert-actions{display:flex;justify-content:flex-end;gap:5px;flex-wrap:wrap;margin-top:5px}.break-alert-dismiss{color:#6c594f}.break-alert-delete-global{color:#a01818!important;border-color:#a01818!important;background:#fff!important}.break-alert-delete-global:disabled{opacity:.55;cursor:wait}.break-alert-hidden-chip,.break-alert-global-off{position:fixed;z-index:1200;display:flex;align-items:center;gap:7px;border:1px solid #9c6a13;border-radius:10px;background:#fff8e8;box-shadow:0 5px 16px rgba(80,58,20,.16);padding:7px 9px;font-size:11px;font-weight:800}.break-alert-hidden-chip button,.break-alert-global-off button{color:#75500c}.break-alert-global-off{right:18px;top:82px;border-color:#6d746f;background:#f4f6f5;color:#34433d}.break-alert-global-off button{color:#34433d}
        @media(max-width:820px){.topbar-title.vera-script-tagline{font-size:23px;line-height:1.05;font-weight:900;color:var(--gold)}.break-alert-stack{width:calc(100vw - 12px);max-height:calc(100vh - 72px)}.break-alert-toolbar{padding:6px}.break-alert-card{padding:7px 8px}.break-alert-global-off{right:6px;top:70px}}
        .sidebar-footer{flex-shrink:0}
        .sidebar .nav-list > :is(a,button){order:0!important}
        @media(max-width:430px){.topbar{flex-wrap:wrap}.topbar-actions{width:100%;justify-content:flex-end}.topbar-title.vera-script-tagline{font-size:20px;white-space:normal}.break-alert-toolbar{align-items:flex-start}.break-alert-toolbar-actions{flex-wrap:wrap;justify-content:flex-end}}
      `}</style>
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${standalone && !standaloneMenuOpen ? 'standalone-hidden' : ''}`}>
        <div className="brand-block">
          <div className="brand-mark">VERA</div>
          <div><div className="brand-name">SPA</div></div>
          <button data-dialog-close="true" data-ui-key="u-8012b53e9821" className="mobile-close icon-button" onClick={() => { setMobileOpen(false); setStandaloneMenuOpen(false) }} aria-label="Đóng menu"><X size={20} /></button>
        </div>

        <div className="menu-caption">MENU</div>
        <nav className="nav-list">
          {items.filter(({ id, permission, anyPermission, adminOnly, roles }) => {
            if (user?.must_change_password && id !== 'profile') return false
            if (roles && !roles.includes(String(user?.role || '').toLowerCase())) return false
            if (user?.role === 'admin') return true
            if (adminOnly) return false
            if (permission && user?.permissions?.[permission] !== true) return false
            if (anyPermission && !anyPermission.some((key) => user?.permissions?.[key] === true)) return false
            return true
          }).map(({ id, label, icon: Icon, ready }) => (
            <a data-ui-key={`u-menu-${id}`}
              key={id}
              className={`nav-item ${(currentPage === id || (currentPage === 'hc-rules' && id === 'rules') || (currentPage === 'checkin-history' && id === 'snapshot') || (['changes', 'storage'].includes(currentPage) && id === 'system') || (['notifications', 'permissions'].includes(currentPage) && id === 'settings') || (['department-payroll', 'payroll-config'].includes(currentPage) && id === 'payroll')) ? 'active' : ''} ${ready ? '' : 'disabled'}`}
              href={ready ? menuPageUrl(id) : '#'}
              onClick={(event) => chooseFromLink(event, id, ready)}
              onPointerEnter={() => { if (ready) onPageIntent?.(id) }}
              onFocus={() => { if (ready) onPageIntent?.(id) }}
              aria-disabled={!ready || undefined}
              title={ready ? `${label} · Có thể nhấp chuột phải để mở tab mới` : 'Sẽ chuyển đổi ở giai đoạn tiếp theo'}
            >
              <Icon size={19} /><span>{label}</span>{!ready && <span className="soon-pill">Sau</span>}
            </a>
          ))}
          {user?.role === 'admin' && !user?.must_change_password && <button data-ui-key="u-783d8360d527"
            type="button" className={`nav-item appearance-menu-item ${layoutDesignerOpen ? 'active' : ''}`}
            ref={layoutTrigger} aria-expanded={layoutDesignerOpen}
            onClick={() => { setLayoutDesignerOpen(true); setMobileOpen(false); setStandaloneMenuOpen(false) }}
          ><Settings2 size={19} /><span>Giao diện</span></button>}
        </nav>

        {!user?.must_change_password && <DevicePushMenu key={user.id}/>}
        <div className="sidebar-footer">
          <div data-ui-key="u-dd74261c77ac" className="user-card">
            <div className="avatar">{(user?.email || 'V')[0].toUpperCase()}</div>
            <div className="user-copy"><strong>{user?.user_metadata?.full_name || user?.email || 'Nhân viên VERA'}</strong><span>{user?.role ? `Vai trò: ${user.role}` : 'Đang đăng nhập'}</span></div>
          </div>
          <button data-ui-key="u-0ef0dfbc34fb" data-ui-label-default="Đăng xuất" className="signout-button" onClick={onSignOut}><LogOut size={18} /><UiCustomText uiKey="u-0ef0dfbc34fb"> Đăng xuất</UiCustomText></button>
        </div>
      </aside>

      {sidebarOpen && <button data-ui-key="u-3833c8d2c1ff" className="sidebar-backdrop" onClick={() => { setMobileOpen(false); setStandaloneMenuOpen(false) }} aria-label="Đóng menu" />}

      <main className="main-area">
        {(currentPage !== 'live-tour' || (isAdmin && !user?.must_change_password)) && <header className="topbar">
          {navigationToggle}
          <button type="button" className="topbar-brand-home" onClick={() => choose('live-tour', true)} disabled={user?.must_change_password} aria-label="Về trang Live Tour" title="Về trang Live Tour"><span className="topbar-kicker">VERA SPA</span><span className="topbar-title vera-script-tagline">Suối nguồn thư giãn, trọn vẹn an yên</span></button>
          <UiToolbar data-ui-key="u-d08e23e02899" className="topbar-actions">
            {!user?.must_change_password && <NotificationInbox key={`system-inbox:${user?.id || user?.employee_username}`} user={user} onPageChange={page => choose(page, true)}/>}
            {currentPage !== 'tour' && currentPage !== 'live-tour' && <button data-ui-key="u-f10aa9b76c0d" data-ui-label-default="Mở tab mới" type="button" className="topbar-refresh-button topbar-open-tab-button" onClick={openCurrentPageInNewTab} aria-label="Mở trang hiện tại trong tab mới" title="Mở trang hiện tại trong tab mới"><ExternalLink size={15} /><UiCustomText uiKey="u-f10aa9b76c0d"> Mở tab mới</UiCustomText></button>}
            <button data-ui-key="u-ae117d0698c3" data-ui-label-default="Làm mới" type="button" className="topbar-refresh-button" onClick={refreshPage} aria-label="Làm mới trang hiện tại" title="Làm mới trang hiện tại"><RefreshCw size={15} /><UiCustomText uiKey="u-ae117d0698c3"> Làm mới</UiCustomText></button>
          </UiToolbar>
        </header>}
        <div className={`page-wrap ${currentPage === 'tour' ? 'tour-page-wrap' : currentPage === 'live-tour' ? 'tour-page-wrap live-tour-page-wrap' : ''}`.trim()}>
          {showPageNotifications && <StableFeedback>
            {refreshMessage && <p role="status">{refreshMessage}</p>}
          {user?.must_change_password && <div className="warning-box first-login-warning">Đây là lần đăng nhập Web V2 đầu tiên. Bạn cần đổi mật khẩu mạnh trước khi sử dụng các chức năng khác.</div>}

          {isAdmin && breakAlertControl.disabled && <div className="break-alert-global-off"><span>Thông báo nghỉ giữa ca đang TẮT cho mọi tài khoản.</span><button data-ui-key="u-c49a507e2857" type="button" disabled={breakAlertControl.busy} onClick={() => toggleGlobalBreakAlerts(false)}>{breakAlertControl.busy ? 'Đang bật…' : 'Bật lại'}</button></div>}
          {birthdayNotice && <NotificationModal title="Sinh nhật" onClose={dismissBirthday}><div className="birthday-notice"><Cake size={19} /><div><strong>Sinh nhật tháng {birthdayNotice.month}</strong><span>{birthdayNotice.today_count ? `Hôm nay có ${birthdayNotice.today_count} sinh nhật. ` : ''}{birthdayNotice.birthdays.map((item) => `${String(item.day).padStart(2, '0')}/${String(birthdayNotice.month).padStart(2, '0')} · ${item.full_name}`).join(' · ')}</span></div><button data-ui-key="u-b80e8fd10ec1" data-ui-label-default="Xem" type="button" onClick={() => { dismissBirthday(); choose('birthday', true) }}><UiCustomText uiKey="u-b80e8fd10ec1">Xem</UiCustomText></button><button data-dialog-close="true" data-ui-key="u-8bb4598c579b" data-ui-label-default="×" type="button" className="birthday-dismiss" onClick={dismissBirthday} aria-label="Đóng"><UiCustomText uiKey="u-8bb4598c579b">×</UiCustomText></button></div></NotificationModal>}
          </StableFeedback>}

          {showPageNotifications && !breakAlertControl.disabled && breakAlerts.length > 0 && breakAlertsHidden && <div className="break-alert-hidden-chip" style={alertPositionStyle}><BellRing size={15} /><span>{breakAlerts.length} cảnh báo đang tạm ẩn</span><button data-ui-key="u-b92d7cc74167" data-ui-label-default="Hiện" type="button" onClick={() => setBreakAlertsHidden(false)}><UiCustomText uiKey="u-b92d7cc74167">Hiện</UiCustomText></button></div>}

          {showPageNotifications && !breakAlertControl.disabled && breakAlerts.length > 0 && !breakAlertsHidden && <NotificationModal title="Cảnh báo chấm công" onClose={() => setBreakAlertsHidden(true)}><div ref={breakAlertStackRef} className="break-alert-stack" style={alertPositionStyle} aria-live="assertive">
            <UiToolbar data-ui-key="u-1b670af1a7f7" className="break-alert-toolbar" onPointerDown={beginAlertDrag} onPointerMove={moveAlertDrag} onPointerUp={endAlertDrag} onPointerCancel={endAlertDrag}>
              <strong>🔔 {breakAlerts.length} cảnh báo</strong>
              <UiToolbar data-ui-key="u-65c033d3e44a" className="break-alert-toolbar-actions">
                <button data-ui-key="u-4e999c8465ec" data-ui-label-default="Ẩn tạm" type="button" onClick={() => setBreakAlertsHidden(true)}><UiCustomText uiKey="u-4e999c8465ec">Ẩn tạm</UiCustomText></button>
                {isAdmin && <button data-ui-key="u-7dbf5823d6e9" type="button" disabled={breakAlertControl.busy} onClick={() => toggleGlobalBreakAlerts(true)}>{breakAlertControl.busy ? 'Đang tắt…' : 'Tắt tất cả'}</button>}
              </UiToolbar>
            </UiToolbar>
            {breakAlerts.map((alert) => <div key={alert.tag || alert.key} className={`break-alert-card ${alert.audience === 'employee' ? 'employee' : ''}`}>
              <BellRing size={16} />
              <div>
                <strong>{alert.kind === 'missing-scheduled-checkin' ? `CHƯA CHECK-IN · ${alert.employee}` : alert.audience === 'staff' ? `VÀO LẠI TRỄ · ${alert.employee}` : `NHẮC VÀO LẠI · ${alert.employee}`}</strong>
                {alert.kind === 'missing-scheduled-checkin' ? <span>{alert.body}</span> : <><span>{alert.break_out} → hạn {alert.deadline} · {alert.planned_minutes} phút.</span><span className="break-alert-timer">{liveAlertTiming(alert, clockMs)}</span></>}
                {isAdmin && <UiToolbar data-ui-key="u-2b13a3616750" className="break-alert-actions">
                  <button data-ui-key="u-cc625132fd3a" data-ui-label-default="Tắt trên máy này" type="button" className="break-alert-dismiss" onClick={() => dismissBreakAlert(alert)}><UiCustomText uiKey="u-cc625132fd3a">Tắt trên máy này</UiCustomText></button>
                  {alert.kind !== 'missing-scheduled-checkin' && <button data-ui-key="u-9f16caed0e55" type="button" className="break-alert-delete-global" disabled={Boolean(deletingBreakAlertTag)} onClick={() => void deleteBreakAlertForAll(alert)}>{deletingBreakAlertTag === alert.tag ? 'Đang xóa…' : 'Xóa cho tất cả'}</button>}
                </UiToolbar>}
              </div>
            </div>)}
          </div></NotificationModal>}


          <PageContent navigationToggle={navigationToggle}>{children}</PageContent>
        </div>
      </main>
      <PageErrorBoundary page={currentPage} onRetry={() => LayoutDesigner.reset()}><Suspense fallback={null}><LayoutDesigner user={user} page={currentPage} initialTab={currentPage === 'appearance' ? 'rooms' : undefined} open={layoutDesignerOpen && !user?.must_change_password} onClose={() => { setLayoutDesignerOpen(false); layoutTrigger.current?.focus({ preventScroll: true }) }}/></Suspense></PageErrorBoundary>
      <BackToTop/>
      {showPageNotifications && !user?.must_change_password && <PopupNotifications key={`system-notifications:${user?.id || user?.employee_username}`} user={user} onPageChange={page => choose(page, true)}/>}
      {<OnlineBookingPopup key={`online-booking:${user?.id || user?.employee_username}`} user={user} onOpen={() => choose('online-bookings', true)}/>}
      <BookingNotificationPopup key={user?.id || user?.username || user?.employee_username || ''} user={user} onOpen={() => choose('live-tour', true)}/>
      <MissingCheckinPopup key={`${user?.username || user?.employee_username || user?.id || ''}:${role}`} user={user}/>
    </div>
  )
}
